import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { getLocalStorage, setLocalStorage } from './aaa';
import { isElementaryStudent } from './UserManagementHelpers';

/**
 * Get the destination URL for a student when they skip the survey
 * @param userProfile - User profile to determine student type
 * @returns Destination path for senior students, or null for elementary (handled separately)
 */
export function getStudentDestination(userProfile: UserProfileType): string | null {
  if (isElementaryStudent(userProfile)) {
    // Elementary students go to /play via window.location.href, not route
    return null;
  }

  // Senior students go to studentportal
  // Check if they've completed check-in to determine specific destination
  const hasCheckedIn = getLocalStorage('isCheckInCompleteOrOptedOut');
  return hasCheckedIn === 'true' ? '/studentportal/toDos' : '/studentportal/checkin';
}

/**
 * Check if user has already made a choice for this campaign
 * @param userId - User ID
 * @param campaignId - Campaign ID to check
 * @returns true if user has made a choice, false otherwise
 */
export function hasUserMadeChoice(userId: string, campaignId: string): boolean {
  if (!userId || !campaignId) return false;

  try {
    const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const userChoices = allChoices[userId] || {};
    const choice = userChoices[campaignId];

    console.log('[hasUserMadeChoice] Checking choice:', {
      userId,
      campaignId,
      hasChoice: !!choice,
      choiceData: choice,
      userChoicesCount: Object.keys(userChoices).length,
      now: Date.now(),
    });

    if (choice && choice.expiresAt > Date.now()) {
      console.log('[hasUserMadeChoice] Valid choice found, returning true');
      return true;
    }

    // Clean up expired choice
    if (choice && choice.expiresAt <= Date.now()) {
      console.log('[hasUserMadeChoice] Choice expired, cleaning up');
      delete userChoices[campaignId];
      allChoices[userId] = userChoices;
      setLocalStorage('surveyChoices', JSON.stringify(allChoices));
    }
  } catch (error) {
    console.error('Error checking user survey choice:', error);
  }

  console.log('[hasUserMadeChoice] No valid choice found, returning false');
  return false;
}

/**
 * Record user's choice for this campaign
 * @param userId - User ID
 * @param campaignId - Campaign ID
 * @param choice - User's choice: 'take' or 'skip'
 */
export function recordUserChoice(userId: string, campaignId: string, choice: 'take' | 'skip'): void {
  if (!userId || !campaignId) return;

  try {
    const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const userChoices = allChoices[userId] || {};

    // Store choice with 2 hour expiry
    userChoices[campaignId] = {
      choice,
      timestamp: Date.now(),
      expiresAt: Date.now() + 2 * 60 * 60 * 1000, // 2 hours
    };

    allChoices[userId] = userChoices;
    setLocalStorage('surveyChoices', JSON.stringify(allChoices));

    console.log('[recordUserChoice] Choice recorded:', {
      userId,
      campaignId,
      choice,
      expiresAt: userChoices[campaignId].expiresAt,
    });
  } catch (error) {
    console.error('Error recording user survey choice:', error);
  }
}

/**
 * Get user's previous choice for this campaign
 * @param userId - User ID
 * @param campaignId - Campaign ID to check
 * @returns Previous choice or null if no valid choice exists
 */
export function getUserChoice(userId: string, campaignId: string): 'take' | 'skip' | null {
  if (!userId || !campaignId) return null;

  try {
    const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const userChoices = allChoices[userId] || {};
    const choice = userChoices[campaignId];

    if (choice && choice.expiresAt > Date.now()) {
      return choice.choice;
    }
  } catch (error) {
    console.error('Error getting user survey choice:', error);
  }

  return null;
}

/**
 * Get the play URL for elementary students
 * @returns Play URL based on environment
 */
export function getPlayUrl(): string {
  if (window.location.hostname === 'localhost') {
    return 'https://test.peekapak.com/play/index.htm';
  } else {
    return `${window.location.protocol}//${window.location.hostname}/play/index.htm`;
  }
}

/**
 * Get the current survey sequence count for the user
 * @param userId - User ID
 * @returns Survey sequence number (1-based index)
 */
export function getSurveySequenceCount(userId: string): number {
  if (!userId) return 1;

  try {
    const allCounts = JSON.parse(getLocalStorage('surveySequenceCount') || '{}');
    const userCount = allCounts[userId];
    return userCount ? parseInt(userCount, 10) : 1;
  } catch (error) {
    // Old format or corrupted data - return default
    console.log('[getSurveySequenceCount] Error or old format detected, returning default:', error);
    return 1;
  }
}

/**
 * Increment the survey sequence count for the user
 * Stores the count with a 24-hour expiry
 * @param userId - User ID
 */
export function incrementSurveySequenceCount(userId: string): void {
  if (!userId) return;

  try {
    const allCounts = JSON.parse(getLocalStorage('surveySequenceCount') || '{}');
    const currentCount = allCounts[userId] || 1;
    allCounts[userId] = currentCount + 1;
    // Store with 24 hour expiry (1440 minutes)
    setLocalStorage('surveySequenceCount', JSON.stringify(allCounts), 1440);

    console.log('[incrementSurveySequenceCount] Count incremented:', {
      userId,
      newCount: allCounts[userId],
    });
  } catch (error) {
    console.error('Error incrementing survey sequence count:', error);
  }
}

/**
 * Reset the survey sequence count for the user (call when survey chain is complete)
 * @param userId - User ID
 */
export function resetSurveySequenceCount(userId: string): void {
  if (!userId) return;

  try {
    const allCounts = JSON.parse(getLocalStorage('surveySequenceCount') || '{}');
    allCounts[userId] = 1;
    // Store with 24 hour expiry (1440 minutes)
    setLocalStorage('surveySequenceCount', JSON.stringify(allCounts), 1440);

    console.log('[resetSurveySequenceCount] Count reset:', {
      userId,
      newCount: 1,
    });
  } catch (error) {
    console.error('Error resetting survey sequence count:', error);
  }
}

/**
 * Get background color for survey details box based on sequence number
 * @param sequenceNumber - Survey sequence number (1-based)
 * @returns Hex color code for background
 */
export function getSurveyBackgroundColor(sequenceNumber: number): string {
  const colors = ['#F5FBFE', '#F0FFE5', '#FFECE4', '#FFF8D9'];
  // Use modulo to cycle through colors (convert to 0-based index)
  return colors[(sequenceNumber - 1) % colors.length];
}

/**
 * Clear a specific campaign choice from user's choices
 * @param userId - User ID
 * @param campaignId - Campaign ID to clear
 */
export function clearUserChoice(userId: string, campaignId: string): void {
  if (!userId || !campaignId) return;

  try {
    const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
    const userChoices = allChoices[userId] || {};

    if (userChoices[campaignId]) {
      console.log('[clearUserChoice] Clearing choice:', { userId, campaignId });
      delete userChoices[campaignId];
      allChoices[userId] = userChoices;
      setLocalStorage('surveyChoices', JSON.stringify(allChoices));
    }
  } catch (error) {
    console.error('Error clearing user choice:', error);
  }
}
